import { useSessionConfigDialog } from "@contexts/DialogContext/dialogs/SessionConfigDialog";
import { usePurchaseDialog } from "@contexts/DialogContext/dialogs/purchase";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppLogo } from "@features/exam/hooks/useAppLogo";
import { images } from "@helpers/images";
import { useAppStore } from "@hooks/useAppStore";
import { isWeb } from "@memorang/helpers/platform";
import Box from "@memorang/ui/components/Box";
import { NavigationRail } from "@memorang/ui/components/Navigation/NavigationRail";
import { NavRailBanner } from "@memorang/ui/components/Navigation/NavigationRail/NavRailBanner";
import { QuickStudyButton } from "@memorang/ui/components/Navigation/QuickStudyButton";
import StoresDownloadBadge from "@memorang/ui/components/StoresDownloadBadge";
import useBreakpoints from "@memorang/ui/useBreakpoints";
import { router, usePathname } from "expo-router";
import { useTranslation } from "react-i18next";
import { Drawer } from "../../../CustomDrawer";
import { drawerWidths } from "../constants";
import { useDrawerNavItems } from "../hooks/useDrawerNavItems";
import { useHasAccess } from "../hooks/useHasAccess";

export const AppDrawer = () => {
	const appLogo = useAppLogo();
	const appLinksIos = useAppStore((s) => s.app.links.ios);
	const appLinksAndroid = useAppStore((s) => s.app.links.android);
	const privacyUrl = useAppStore((store) => store.app.links.privacy);
	const tosUrl = useAppStore((store) => store.app.links.tos);
	const isStudyPackEnabled = useAppStore((s) => s.app.isStudyPackEnabled);

	const isTokenBasedFlow = useAppStore((s) => s.app.auth?.tokenBasedFlow);
	const { username } = useAuthContext();
	const {
		currentExamContext: { studyPackCollectionId },
		isExamContextLoading,
	} = useExamContext();
	const { smUp, mdDown } = useBreakpoints();
	const { showSessionConfig } = useSessionConfigDialog();
	const { items, routeMap } = useDrawerNavItems();
	const { hasStudyPackPurchaseAccess, isAccessPending } = useHasAccess();
	const { t } = useTranslation();
	const { showPurchase } = usePurchaseDialog();

	const isCollapsed = smUp && mdDown;

	const showStudyPackViews =
		isStudyPackEnabled && Boolean(studyPackCollectionId);

	const pathname = usePathname();
	const activeItemKey = pathname.split("/")[1] || "";

	const shouldShowNavRailBanner = Boolean(
		!isAccessPending &&
			!hasStudyPackPurchaseAccess &&
			!isTokenBasedFlow &&
			showStudyPackViews,
	);

	return (
		<Box flex={1} overflow="hidden">
			<Drawer
				backBehavior={isWeb ? "fullHistory" : undefined}
				drawerContent={() => (
					<NavigationRail
						logo={appLogo}
						items={items}
						activeItemKey={activeItemKey}
						isCollapsed={isCollapsed}
						privacyNoticeUrl={privacyUrl}
						mobileAppTermsUrl={tosUrl}
						onPressItem={(key) => {
							const route = routeMap[key];
							if (route) {
								router.push(route);
							}
						}}
						onPressLogo={() => router.push("/home")}
						onPressAccount={() => router.push("/account/settings")}
						username={username}
						renderFab={() => (
							<QuickStudyButton
								enabled={showStudyPackViews}
								isLoading={
									isStudyPackEnabled &&
									isExamContextLoading &&
									!studyPackCollectionId
								}
								isCollapsed={isCollapsed}
								onPress={() => {
									showSessionConfig({});
								}}
							/>
						)}
						renderFooter={
							isCollapsed
								? undefined
								: () => (
										<Box gap={12}>
											{shouldShowNavRailBanner && (
												<NavRailBanner
													title={t("dashboard.actions.unlock")}
													subtitle={t("dashboard.actions.unlockTooltip")}
													ctaLabel={t("dashboard.actions.upgradeToPremium")}
													onPress={() =>
														showPurchase({ productType: "STUDY_PACK" })
													}
												/>
											)}
											<StoresDownloadBadge
												ios={{
													badge: images.appStores.ios,
													href: appLinksIos,
												}}
												android={{
													badge: images.appStores.android,
													href: appLinksAndroid,
												}}
											/>
										</Box>
									)
						}
					/>
				)}
				screenOptions={{
					drawerType: smUp ? "permanent" : "slide",
					headerTitle: "",
					drawerStyle: {
						width: isCollapsed ? drawerWidths.collapsed : drawerWidths.expanded,
						borderRightColor: "transparent",
					},
					header: () => null,
				}}
			/>
		</Box>
	);
};
